import { familyContentReporterContext } from '@chorepop/core/client';
import type { Component } from 'solid-js';
import { For, Show, createSignal } from 'solid-js';
import { submitFamilyContentReport } from '../cloud/cloudAuth';
import { ActionScreen } from '../components/ActionScreen';
import type { ReportConcernContext } from '../lib/reportConcern';

type Props = {
  sessionId: string;
  context?: ReportConcernContext;
  onBack: () => void;
};

const CONTENT_KINDS = [
  { value: 'account', label: 'A family member or profile' },
  { value: 'chore', label: 'A chore' },
  { value: 'routine', label: 'A routine or routine step' },
  { value: 'goal', label: 'A goal' },
  { value: 'deposit', label: 'Money added' },
  { value: 'withdrawal', label: 'Money spent' },
  { value: 'approval', label: 'An approval or reply' },
  { value: 'other', label: 'Something else' },
] as const;

const REASONS = [
  { value: 'bullying_or_harassment', label: 'Bullying or unkind behaviour' },
  { value: 'threat_or_safety', label: 'A threat or safety concern' },
  { value: 'personal_information', label: 'Private information was shared' },
  { value: 'inappropriate_language', label: 'Inappropriate words or content' },
  { value: 'sexual_or_child_safety', label: 'Sexual content or child-safety concern' },
  { value: 'hate_or_discrimination', label: 'Hate or discrimination' },
  { value: 'violence_or_illegal_content', label: 'Violence or illegal content' },
  { value: 'something_else', label: 'Something else' },
] as const;

export const ReportConcernScreen: Component<Props> = (props) => {
  const [contentKind, setContentKind] = createSignal<(typeof CONTENT_KINDS)[number]['value']>(
    props.context?.contentKind ?? 'other',
  );
  const [reasonCode, setReasonCode] =
    createSignal<(typeof REASONS)[number]['value']>('bullying_or_harassment');
  const [contentDescription, setContentDescription] = createSignal('');
  const [submitting, setSubmitting] = createSignal(false);
  const [errorMessage, setErrorMessage] = createSignal<string | null>(null);
  const [confirmation, setConfirmation] = createSignal<string | null>(null);

  const submit = async () => {
    const description = contentDescription().trim();
    if (description.length < 3) {
      setErrorMessage('Tell us what to review so we can find the right words or item.');
      return;
    }
    setSubmitting(true);
    setErrorMessage(null);
    try {
      const reporter = await familyContentReporterContext(props.sessionId);
      if (!reporter.ok) throw new Error(reporter.error.message);
      const reportId = await submitFamilyContentReport({
        reporterLocalAccountId: reporter.value.accountId,
        reporterRole: reporter.value.roleCode,
        ...(props.context?.reportedLocalAccountId !== undefined
          ? { reportedLocalAccountId: props.context.reportedLocalAccountId }
          : {}),
        contentKind: contentKind(),
        ...(props.context?.contentLocalId ? { contentLocalId: props.context.contentLocalId } : {}),
        reasonCode: reasonCode(),
        reporterNote: description,
        contentSnapshot: {
          ...(props.context?.contentSnapshot ?? { reportedDescription: description }),
          reportContextLabel: props.context?.label ?? 'Manual report',
          reporterDisplayName: reporter.value.displayName,
          source: props.context ? 'contextual_report_action' : 'in_app_report_concern',
        },
      });
      setConfirmation(reportId);
    } catch (error) {
      setErrorMessage(
        error instanceof Error ? error.message : 'Could not send the private report.',
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <ActionScreen
      eyebrow="Private family safety"
      title="Report unsafe content"
      onBack={props.onBack}
      footer={
        <Show when={!confirmation()}>
          <button
            type="button"
            class="primary action-screen-primary"
            disabled={submitting()}
            onClick={() => void submit()}
          >
            {submitting() ? 'Sending…' : 'Send private report'}
          </button>
        </Show>
      }
    >
      <Show
        when={!confirmation()}
        fallback={
          <section class="form-card" aria-live="polite">
            <h2>Report sent</h2>
            <p>
              ChorePop received it privately. The person you reported will not be told who sent it.
            </p>
            <p class="field-hint">Confirmation: {confirmation()}</p>
            <button type="button" class="secondary" onClick={props.onBack}>
              Done
            </button>
          </section>
        }
      >
        <p class="action-screen-intro">
          Tell ChorePop about unsafe or unkind words shared inside your family. This report goes to
          ChorePop, not to the family member you are reporting.
        </p>
        <Show when={props.context}>
          {(context) => (
            <section class="form-card report-context-card" aria-label="Item being reported">
              <span class="field-label">You are reporting</span>
              <strong>{context().label}</strong>
              <p class="field-hint">
                ChorePop has attached the exact item and its current wording to this private report.
              </p>
            </section>
          )}
        </Show>
        <section class="form-card">
          <label class="field" for="report-content-kind">
            <span class="field-label">Where did you see it?</span>
            <select
              id="report-content-kind"
              value={contentKind()}
              disabled
              onChange={(event) =>
                setContentKind(event.currentTarget.value as (typeof CONTENT_KINDS)[number]['value'])
              }
            >
              <For each={CONTENT_KINDS}>
                {(kind) => <option value={kind.value}>{kind.label}</option>}
              </For>
            </select>
          </label>
          <label class="field" for="report-reason">
            <span class="field-label">What is wrong?</span>
            <select
              id="report-reason"
              value={reasonCode()}
              disabled={submitting()}
              onChange={(event) =>
                setReasonCode(event.currentTarget.value as (typeof REASONS)[number]['value'])
              }
            >
              <For each={REASONS}>
                {(reason) => <option value={reason.value}>{reason.label}</option>}
              </For>
            </select>
          </label>
          <label class="field" for="report-description">
            <span class="field-label">What should ChorePop review?</span>
            <textarea
              id="report-description"
              class="field-textarea"
              rows={5}
              maxlength={1000}
              value={contentDescription()}
              disabled={submitting()}
              placeholder={
                props.context
                  ? 'Tell us what is wrong with this item.'
                  : 'Include the exact words and where they appear.'
              }
              onInput={(event) => setContentDescription(event.currentTarget.value)}
            />
            <span class="field-hint">Do not add passwords, PINs, or payment details.</span>
          </label>
        </section>
        <Show when={errorMessage()}>
          {(message) => (
            <p class="form-error" role="alert">
              {message()}
            </p>
          )}
        </Show>
      </Show>
    </ActionScreen>
  );
};
